<template>
  <!-- Plan 61: students excused week after week, and break check-ins the teacher asked for, at the
       top of the Scheduling tab (the daily digest links here). Answers go to /series-stops;
       nothing renders when there's nothing to answer. -->
  <div v-if="streaks.length || followUps.length" class="space-y-2 mb-6">
    <h3 class="text-xs font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-widest mb-2">
      Student check-ins ({{ streaks.length + followUps.length }})
    </h3>
    <div v-for="s in streaks" :key="'s' + s.id" class="card border-amber-300 dark:border-amber-700 p-3 text-sm">
      <p class="text-fg-base dark:text-fg-base-dark">
        <strong>{{ s.student }}</strong> has been excused {{ s.weeks }} lessons in a row (since {{ fmtDate(s.since) }})
        and still has {{ s.future_lessons }} lessons on your calendar ({{ s.schedule }}). What's going on?
      </p>
      <template v-if="openId !== 's' + s.id">
        <div class="flex flex-wrap gap-1.5 mt-2">
          <button :class="btn" @click="start('s' + s.id, 'break')">On a break</button>
          <button :class="btn" @click="start('s' + s.id, 'stopped')">They've stopped</button>
          <button :class="btn" :disabled="busy" @click="answer(s, 'still_coming')">Still coming</button>
          <button :class="btn" :disabled="busy" @click="answer(s, 'ask_family')">Not sure — ask the family</button>
        </div>
      </template>
      <div v-else class="mt-3 space-y-3">
        <template v-if="mode === 'break'">
          <div>
            <label :class="labelClass">Remind me to check back around <span class="font-normal text-fg-faint">(optional)</span></label>
            <input v-model="stop.follow_up_date" type="date" :min="tomorrowIso" :class="inputClass" />
          </div>
          <div>
            <label :class="labelClass">Note <span class="font-normal text-fg-faint">(optional)</span></label>
            <input v-model="stop.note" type="text" placeholder="e.g. back after hockey season" :class="inputClass" />
          </div>
          <p class="text-xs text-fg-muted dark:text-fg-muted-dark">Their upcoming lessons come off your calendar so the time is free.</p>
        </template>
        <StopReasonFields v-else v-model="stop" :allow-break="false" />
        <div class="flex gap-2">
          <button :class="primary" :disabled="busy" @click="answer(s, mode)">{{ mode === 'break' ? 'Free the time' : 'End lessons' }}</button>
          <button :class="btn" @click="openId = null">Back</button>
        </div>
      </div>
    </div>

    <div v-for="f in followUps" :key="'f' + f.id" class="card border-blue-300 dark:border-blue-700 p-3 text-sm">
      <p class="text-fg-base dark:text-fg-base-dark">
        Time to check back in with <strong>{{ f.student }}</strong> ({{ f.schedule }}<span v-if="f.ended">, on a break since {{ fmtDate(f.ended) }}</span>).
        <span v-if="f.note" class="text-fg-muted dark:text-fg-muted-dark">“{{ f.note }}”</span>
      </p>
      <template v-if="openId !== 'f' + f.id">
        <div class="flex flex-wrap gap-1.5 mt-2">
          <button :class="primary" @click="startAgain(f)">Start lessons again</button>
          <button :class="btn" @click="start('f' + f.id, 'not_returning')">Not returning</button>
          <button :class="btn" @click="start('f' + f.id, 'later')">Remind me later</button>
        </div>
      </template>
      <div v-else class="mt-3 space-y-3">
        <div v-if="mode === 'later'">
          <label :class="labelClass">Remind me on</label>
          <input v-model="stop.follow_up_date" type="date" :min="tomorrowIso" :class="inputClass" />
        </div>
        <StopReasonFields v-else v-model="stop" :allow-break="false" />
        <div class="flex gap-2">
          <button :class="primary" :disabled="busy" @click="followUp(f)">Save</button>
          <button :class="btn" @click="openId = null">Back</button>
        </div>
      </div>
    </div>
    <p v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useApi } from '../../composables/useApi.js'
import { lessonsChange, notifyLessonsChanged } from '../../composables/useCalendarData.js'
import { emptyStop, openReschedule } from '../../utils/stopReasons.js'
import StopReasonFields from './StopReasonFields.vue'

const { fetchApi } = useApi()

const streaks = ref([])
const followUps = ref([])
const openId = ref(null)   // 's<id>' / 'f<id>' whose form is open
const mode = ref('')
const stop = ref(emptyStop())
const busy = ref(false)
const error = ref('')

async function load() {
  try {
    const data = await fetchApi('/api/series-stops/mine')
    streaks.value = data.streaks
    followUps.value = data.follow_ups
  } catch { /* not shown when it can't load */ }
}
load()
watch(lessonsChange, load)

function start(id, m) {
  openId.value = id
  mode.value = m
  stop.value = emptyStop()
  error.value = ''
}

async function post(url, body) {
  busy.value = true
  error.value = ''
  try {
    await fetchApi(url, { method: 'POST', body: JSON.stringify(body) })
    openId.value = null
    return true
  } catch (e) {
    error.value = e.message || 'Something went wrong.'
    return false
  } finally {
    busy.value = false
  }
}

async function answer(s, response) {
  if (response === 'stopped' && !stop.value.category) { error.value = "Pick why they've stopped."; return }
  const ok = await post(`/api/series-stops/streaks/${s.id}/answer`, {
    response,
    category: response === 'stopped' ? stop.value.category : null,
    note: stop.value.note.trim() || null,
    follow_up_date: response === 'break' ? (stop.value.follow_up_date || null) : null,
  })
  if (!ok) return
  streaks.value = streaks.value.filter(x => x.id !== s.id)
  if (response === 'break' || response === 'stopped') notifyLessonsChanged()
}

async function followUp(f) {
  const later = mode.value === 'later'
  if (later && !stop.value.follow_up_date) { error.value = 'Pick a date.'; return }
  if (!later && !stop.value.category) { error.value = "Pick why they aren't coming back."; return }
  const ok = await post(`/api/series-stops/follow-ups/${f.id}`, {
    action: mode.value,
    category: later ? null : stop.value.category,
    note: stop.value.note.trim() || null,
    follow_up_date: later ? stop.value.follow_up_date : null,
  })
  if (ok) followUps.value = followUps.value.filter(x => x.id !== f.id)
}

// New Lesson prefilled from the old series; saving it closes this follow-up (backend).
const startAgain = (f) => openReschedule({ replacesSeriesId: f.series_id })

const tomorrow = new Date()
tomorrow.setDate(tomorrow.getDate() + 1)
const tomorrowIso = tomorrow.toISOString().slice(0, 10)

const fmtDate = (iso) => iso
  ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  : ''

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark disabled:opacity-50'
const primary = 'px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white disabled:opacity-50'
const labelClass = 'block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1'
const inputClass = 'w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-2 focus:ring-blue-500'
</script>
